/**
 * PackagePickerBar — drawer toolbar control: load a library package into the
 * DRAFT (review-first — nothing saved, no assignment recorded; the live
 * preview shifts and Save Config persists). Doubles as the attribution
 * display: the select shows the draft's current package. Loading works on
 * every tracker; "Generate with AI" only where the library API serves the
 * tracker (libraryProviderOf).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  FlowOptimizerConfig,
  FlowOptimizerPackageExpanded,
  TrackerId,
} from '@trafficpulse/shared';

import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import { ConfirmDialog } from '@/components/ui/layout/ConfirmDialog';
import { useToast } from '@/providers/ToastProvider';
import { libraryProviderOf } from '@/features/trackers/flow-optimizer/library-provider';

import { AiGenerateButton } from '../ai';
import { getPackage } from '../api/client/packages-api';
import { useLibraryPackages } from '../api/hooks/useLibraryPackages';
import {
  applyAssembledToDraft,
  applyExpandedPackageToDraft,
  draftHasWork,
} from './apply-package-to-draft';
import PackageOption from './labels/PackageOption';
import PackageTriggerLabel from './labels/PackageTriggerLabel';

export interface PackagePickerBarProps {
  draft: FlowOptimizerConfig;
  onChange: (next: FlowOptimizerConfig) => void;
  provider: TrackerId;
}

const PackagePickerBar: FC<PackagePickerBarProps> = ({
  draft,
  onChange,
  provider,
}) => {
  const toast = useToast();
  const packages = useLibraryPackages();
  const [pending, setPending] = useState<FlowOptimizerPackageExpanded | null>(null);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const aiProvider = libraryProviderOf(provider);

  const applyNow = (pkg: FlowOptimizerPackageExpanded) => {
    onChange(applyExpandedPackageToDraft(draft, pkg));
    setLoadedName(pkg.name);
    setPending(null);
  };

  const fetchDetail = useMutation({
    mutationFn: getPackage,
    onSuccess: (pkg) => (draftHasWork(draft) ? setPending(pkg) : applyNow(pkg)),
    onError: () => toast.error('Package failed to load', 'Try again in a moment.'),
  });

  const options = (packages.data ?? []).map((pkg) => ({
    value: pkg.id,
    label: pkg.name,
    optionContent: <PackageOption pkg={pkg} />,
    // The two-line card overflows the h-7 trigger — collapse to dot + name.
    triggerContent: <PackageTriggerLabel pkg={pkg} />,
  }));

  return (
    <div className="flex min-w-0 items-center gap-2">
      <SearchableSelect
        options={options}
        value={draft.packageId ?? ''}
        onChange={(id) => id && fetchDetail.mutate(id)}
        placeholder={packages.isLoading ? 'Loading…' : 'Load a package…'}
        searchPlaceholder="Search packages…"
        disabled={fetchDetail.isPending || options.length === 0}
        className="h-7 w-52 text-[11px]"
      />
      {aiProvider ? (
        <AiGenerateButton
          provider={aiProvider}
          campaignId={draft.campaignId}
          campaignLocked
          draftApply={(assembled) =>
            onChange(applyAssembledToDraft(draft, assembled))
          }
          className="h-7 px-2 text-[11px]"
        />
      ) : null}
      {loadedName ? (
        <span className="hidden truncate text-[11px] text-muted-foreground lg:inline">
          Loaded — review the live preview, then Save Config.
        </span>
      ) : null}
      <ConfirmDialog
        open={pending !== null}
        title="Load package into draft?"
        description={
          pending
            ? `"${pending.name}" replaces the draft's formula groups and rules. ` +
              'Nothing is saved until you press Save Config.'
            : ''
        }
        confirmLabel="Load package"
        onConfirm={() => pending && applyNow(pending)}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default PackagePickerBar;
